import type { ComponentType } from "react";
import { matchPath } from "react-router";
import type { Flag } from "./flags";

export type Role = "ADMIN" | "TESTER" | "VIEWER";
export type NavSection = "testing" | "insights" | "admin" | "bottom" | null;
export type LayoutKind = "app" | "auth" | "report" | "shared" | "sharedReport";

export interface RouteDef {
  id: string;
  path: string;
  title: string;
  layout: LayoutKind;
  section: NavSection;
  roles?: Role[];
  flag?: Flag;
  needsRuntime?: boolean;
  public?: boolean;
  load: () => Promise<{ default: ComponentType }>;
}

export const ROUTES: RouteDef[] = [
  {
    id: "signIn",
    path: "/sign-in",
    title: "Sign in",
    layout: "auth",
    section: null,
    public: true,
    load: () => import("@/features/auth/SignInPage"),
  },
  {
    id: "waiting",
    path: "/waiting",
    title: "You're on the list",
    layout: "auth",
    section: null,
    public: true,
    load: () => import("@/features/auth/WaitingPage"),
  },
  {
    id: "noAccess",
    path: "/no-access",
    title: "You don't have access to ATS",
    layout: "auth",
    section: null,
    public: true,
    load: () => import("@/features/auth/NoAccessPage"),
  },
  {
    id: "dashboard",
    path: "/",
    title: "Dashboard",
    layout: "app",
    section: "testing",
    load: () => import("@/features/dashboard/DashboardPage"),
  },
  {
    id: "newRun",
    path: "/runs/new",
    title: "New run",
    layout: "app",
    section: "testing",
    roles: ["TESTER", "ADMIN"],
    needsRuntime: true,
    load: () => import("@/features/new-run/NewRunPage"),
  },
  {
    id: "runs",
    path: "/runs",
    title: "Runs",
    layout: "app",
    section: "testing",
    load: () => import("@/features/runs/RunsPage"),
  },
  {
    id: "report",
    path: "/runs/:runId/report",
    title: "ATS Run Report",
    layout: "report",
    section: null,
    needsRuntime: true,
    load: () => import("@/features/report/ReportPage"),
  },
  {
    id: "execution",
    path: "/runs/:runId/executions/:executionId",
    title: "Execution",
    layout: "app",
    section: null,
    needsRuntime: true,
    load: () => import("@/features/execution/ExecutionPage"),
  },
  {
    id: "run",
    path: "/runs/:runId",
    title: "Run",
    layout: "app",
    section: null,
    needsRuntime: true,
    load: () => import("@/features/live-run/RunPage"),
  },
  {
    id: "sharedReport",
    path: "/shared/runs/:runId/report",
    title: "ATS Run Report",
    layout: "sharedReport",
    section: null,
    load: () => import("@/features/report/ReportPage"),
  },
  {
    id: "sharedExecution",
    path: "/shared/runs/:runId/executions/:executionId",
    title: "Execution",
    layout: "shared",
    section: null,
    load: () => import("@/features/execution/ExecutionPage"),
  },
  {
    id: "sharedRun",
    path: "/shared/runs/:runId",
    title: "Run",
    layout: "shared",
    section: null,
    load: () => import("@/features/live-run/RunPage"),
  },
  {
    id: "catalog",
    path: "/catalog",
    title: "Test catalog",
    layout: "app",
    section: "testing",
    needsRuntime: true,
    load: () => import("@/features/catalog/CatalogPage"),
  },
  {
    id: "test",
    path: "/catalog/:testId",
    title: "Test",
    layout: "app",
    section: null,
    needsRuntime: true,
    load: () => import("@/features/catalog/TestDetailPage"),
  },
  {
    id: "profiles",
    path: "/profiles",
    title: "Profiles",
    layout: "app",
    section: "testing",
    needsRuntime: true,
    load: () => import("@/features/profiles/ProfilesPage"),
  },
  {
    id: "profile",
    path: "/profiles/:profileId",
    title: "Profile",
    layout: "app",
    section: null,
    needsRuntime: true,
    load: () => import("@/features/profiles/ProfileDetailPage"),
  },
  {
    id: "artifacts",
    path: "/artifacts",
    title: "Agent builds",
    layout: "app",
    section: "testing",
    needsRuntime: true,
    load: () => import("@/features/artifacts/ArtifactsPage"),
  },
  {
    id: "artifact",
    path: "/artifacts/:artifactId",
    title: "Agent build",
    layout: "app",
    section: null,
    needsRuntime: true,
    load: () => import("@/features/artifacts/ArtifactDetailPage"),
  },
  {
    id: "compare",
    path: "/compare",
    title: "Compare runs",
    layout: "app",
    section: "insights",
    flag: "compare",
    needsRuntime: true,
    load: () => import("@/features/compare/ComparePage"),
  },
  {
    id: "certification",
    path: "/certification",
    title: "Certification",
    layout: "app",
    section: "insights",
    flag: "certification",
    load: () => import("@/features/certification/CertificationPage"),
  },
  {
    id: "flaky",
    path: "/insights/flaky",
    title: "Flaky tests",
    layout: "app",
    section: "insights",
    flag: "flaky",
    load: () => import("@/features/flaky/FlakyPage"),
  },
  {
    id: "schedules",
    path: "/schedules",
    title: "Schedules",
    layout: "app",
    section: "insights",
    flag: "schedules",
    load: () => import("@/features/schedules/SchedulesPage"),
  },
  {
    id: "payloadDiff",
    path: "/insights/payload-diff",
    title: "Payload diff",
    layout: "app",
    section: "insights",
    flag: "payloadDiff",
    load: () => import("@/features/payload-diff/PayloadDiffPage"),
  },
  {
    id: "admin",
    path: "/admin",
    title: "Admin dashboard",
    layout: "app",
    section: "admin",
    roles: ["ADMIN"],
    load: () => import("@/features/admin-dashboard/AdminDashboardPage"),
  },
  {
    id: "adminUsers",
    path: "/admin/users",
    title: "Users",
    layout: "app",
    section: "admin",
    roles: ["ADMIN"],
    load: () => import("@/features/admin-users/UsersPage"),
  },
  {
    id: "adminUser",
    path: "/admin/users/:userId",
    title: "User",
    layout: "app",
    section: null,
    roles: ["ADMIN"],
    load: () => import("@/features/admin-users/UserDetailPage"),
  },
  {
    id: "adminAudit",
    path: "/admin/audit",
    title: "Audit log",
    layout: "app",
    section: "admin",
    roles: ["ADMIN"],
    load: () => import("@/features/admin-audit/AuditPage"),
  },
  {
    id: "adminSettings",
    path: "/admin/settings/:section?",
    title: "Installation settings",
    layout: "app",
    section: "admin",
    roles: ["ADMIN"],
    load: () => import("@/features/admin-settings/InstallationSettingsPage"),
  },
  {
    id: "adminMachines",
    path: "/admin/machines",
    title: "Machines",
    layout: "app",
    section: "admin",
    roles: ["ADMIN"],
    flag: "machines",
    load: () => import("@/features/admin-machines/MachinesPage"),
  },
  {
    id: "adminWaivers",
    path: "/admin/waivers",
    title: "Known-issue waivers",
    layout: "app",
    section: "admin",
    roles: ["ADMIN"],
    flag: "waivers",
    load: () => import("@/features/admin-waivers/WaiversPage"),
  },
  {
    id: "settings",
    path: "/settings/:section?",
    title: "Settings",
    layout: "app",
    section: "bottom",
    load: () => import("@/features/settings/SettingsPage"),
  },
  {
    id: "profilePage",
    path: "/profile/:userId?",
    title: "Profile",
    layout: "app",
    section: null,
    flag: "profilePage",
    load: () => import("@/features/settings/ProfilePage"),
  },
  { id: "help", path: "/help", title: "Help", layout: "app", section: "bottom", load: () => import("@/features/help/HelpPage") },
  {
    id: "notFound",
    path: "*",
    title: "Page not found",
    layout: "app",
    section: null,
    load: () => import("@/features/system/NotFoundPage"),
  },
];

export function routeById(id: string): RouteDef {
  const r = ROUTES.find((x) => x.id === id);
  if (!r) throw new Error(`unknown route ${id}`);
  return r;
}

export function routePath(id: string, params: Record<string, string> = {}): string {
  const path = routeById(id).path.replace(/\/:(\w+)(\?)?/g, (_m, key: string, optional?: string) => {
    const v = params[key];
    if (v === undefined) {
      if (optional) return "";
      throw new Error(`missing param ${key} for ${id}`);
    }
    return `/${encodeURIComponent(v)}`;
  });
  return path === "" ? "/" : path;
}

const NO_RUNTIME_LAYOUTS: LayoutKind[] = ["auth", "shared", "sharedReport"];

// Pages that never read runtime state skip the localhost probe, so a developer opening a share link gets no local network prompt.
export function probesRuntime(pathname: string): boolean {
  const route = ROUTES.find((r) => r.path !== "*" && matchPath(r.path, pathname));
  return !route || !NO_RUNTIME_LAYOUTS.includes(route.layout);
}
